import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select, Typography } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react'
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { formatDobForAPI } from '../../../helpers/common-uwinhelper';
import { uwinAPI } from '../../../services/uwin.api';
import { toast } from 'react-toastify';

const DeclarePregnancy = (props) => {
    const { isModalOpen, isModalClose, profileData, benProfileData, getPregnancyData, getBeneficiaryProfile } = props;

    const [facilitySitesData, setFacilitySitesData] = useState([]);

    useEffect(() => {
        const fetchFacilitySites = async () => {
            try {
                const res = await uwinAPI.getFacilitySessionSites();
                if (res?.success) {
                    setFacilitySitesData(res?.data || []);
                }
            } catch (error) {
                console.error("Failed to fetch facility session sites", error);
            }
        };
        fetchFacilitySites();
    }, []);

    // Calculate min and max dates
    const dateConstraints = (selectedLmcDate) => {
        const currentDate = dayjs();

        // lmc_date constraints
        let lmcMinDate = currentDate.subtract(1, 'year');
        const lmcMaxDate = currentDate;

        // Logic Change: If previous pregnancy exists, min LMC must be 1 month after it
        if (benProfileData?.previous_pregnancy_year) {
            const prevPregDate = dayjs(benProfileData.previous_pregnancy_year);
            if (prevPregDate.isValid()) {
                lmcMinDate = prevPregDate.add(1, 'month');
            }
        }

        // previous_pregnancy_year constraints
        let prevPregnancyMinDate = null;

        // previous_pregnancy_year Logic for max date
        let prevPregnancyMaxDate = currentDate; // Default if no LMC
        if (selectedLmcDate) {
            prevPregnancyMaxDate = dayjs(selectedLmcDate).subtract(30, 'day');
        }

        if (profileData?.dob) {
            // Parse DOB from DD-MMM-YYYY format
            const dobParsed = dayjs(profileData.dob, ['DD-MMM-YYYY', 'D-MMM-YYYY']);
            if (dobParsed.isValid()) {
                prevPregnancyMinDate = dobParsed.add(12, 'year');
            }
        }

        return {
            lmcMinDate,
            lmcMaxDate,
            prevPregnancyMinDate,
            prevPregnancyMaxDate
        };
    };

    // Initial values
    const getInitialValues = () => {
        return {
            lmc_date: profileData?.lmc_date || null,
            blood_group: profileData?.blood_group || '',
            // previous_pregnancy_year: null,
            previous_pregnancy_year: benProfileData?.previous_pregnancy_year || null,
            td_doses_received: '0',
            facility_id: '',
            session_site_id: ''
        };
    };

    // Validation schema
    const getValidationSchema = (constraints) => {
        return Yup.object().shape({
            lmc_date: Yup.date()
                .required('Date of Last Menstrual Period is required')
                .min(
                    constraints?.lmcMinDate.toDate(),
                    `Date must be after ${constraints?.lmcMinDate.format('DD-MMM-YYYY')}`
                )
                .max(
                    constraints?.lmcMaxDate.toDate(),
                    `Date cannot be in the future`
                ),

            blood_group: Yup.string().required('Blood Group is required'),
            facility_id: Yup.string().required('Facility is required'),
            session_site_id: Yup.string().required('Session Site is required'),

            previous_pregnancy_year: Yup.date()
                .nullable()
                .test('min-date', function (value) {
                    if (!value) return true;
                    if (!constraints?.prevPregnancyMinDate) {
                        return this.createError({
                            message: 'Cannot validate without valid date of birth'
                        });
                    }
                    if (dayjs(value).isBefore(constraints?.prevPregnancyMinDate)) {
                        return this.createError({
                            message: `Date must be at least 12 years after date of birth (${constraints?.prevPregnancyMinDate.format('DD-MMM-YYYY')})`
                        });
                    }
                    return true;
                })
                .test('max-date', function (value) {
                    if (!value) return true;
                    // Re-calculate max date inside test to ensure it's always fresh against the current lmc_date
                    if (dayjs(value).isAfter(constraints?.prevPregnancyMaxDate)) {
                        const errorMsg = this.parent.lmc_date
                            ? `Date must be at least 42 days before LMC (${constraints.prevPregnancyMaxDate.format('DD-MMM-YYYY')})`
                            : `Date cannot be in the future`;
                        return this.createError({ message: errorMsg });
                    }
                    return true;
                }),
            td_doses_received: Yup.string().when(['lmc_date', 'previous_pregnancy_year'], {
                is: (lmc_date, previous_pregnancy_year) => {
                    if (!lmc_date || !previous_pregnancy_year) return false;
                    const diff = dayjs(lmc_date).diff(dayjs(previous_pregnancy_year), 'year', true);
                    return diff < 3;
                },
                then: (schema) => schema.required('TD doses received is required when gap is less than 3 years'),
                otherwise: (schema) => schema
            })
        });
    };

    // Check if td_doses field should be shown
    const shouldShowTdDoses = (lmc_date, previous_pregnancy_year) => {
        if (!lmc_date || !previous_pregnancy_year) return false;
        const diff = dayjs(lmc_date).diff(dayjs(previous_pregnancy_year), 'year', true);
        return diff < 3;
    };

    // Handle form submission
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                beneficiary_id: profileData?.beneficiary_id,
                lmc_date: values?.lmc_date ? dayjs(values?.lmc_date).format('YYYY-MM-DD') : null,
                blood_group: values?.blood_group || null,
                previous_pregnancy_year: values?.previous_pregnancy_year
                    ? dayjs(values?.previous_pregnancy_year).format('YYYY-MM-DD')
                    : null,
                session_site_id: values?.session_site_id,
                td_doses_received: values?.td_doses_received,
                platform: "Web"
            };

            const response = await uwinAPI.declarePregnancy(payload);

            if (response?.success) {
                toast.success(response?.message || 'Pregnancy declared successfully');
                getBeneficiaryProfile();
                getPregnancyData();
                isModalClose();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'An error occurred while declaring pregnancy');
        } finally {
            setSubmitting(false);
        }
    };

    // Check if fields should be disabled (pre-filled from profileData)
    const isLmcDateDisabled = !!profileData?.lmc_date;
    const isBloodGroupDisabled = !!profileData?.blood_group;

    return (
        <Dialog
            open={isModalOpen}
            onClose={isModalClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px" } }}
        >
            <DialogTitle>
                Declare Pregnancy
                <IconButton
                    onClick={isModalClose}
                    sx={{ position: "absolute", right: 16, top: 16 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 4, pb: 4 }} dividers>
                <p>
                    Declare pregnancy to avail scheduled vaccinations on time
                </p>
                <Formik
                    initialValues={getInitialValues()}
                    // validationSchema={getValidationSchema()}
                    validate={(values) => {
                        const constraints = dateConstraints(values?.lmc_date);
                        try {
                            getValidationSchema(constraints).validateSync(values, { abortEarly: false });
                        } catch (err) {
                            return err.inner.reduce((acc, curr) => ({ ...acc, [curr.path]: curr.message }), {});
                        }
                    }}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
                        // Get current constraints for DatePickers
                        const currentConstraints = dateConstraints(values?.lmc_date);

                        return (
                            <Form>
                                <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl
                                            fullWidth
                                            error={touched?.facility_id && Boolean(errors?.facility_id)}
                                        >
                                            <InputLabel id="facility-label">Facility <span className="text-danger">*</span></InputLabel>
                                            <Select
                                                labelId="facility-label"
                                                id="facility_id"
                                                name="facility_id"
                                                value={values?.facility_id}
                                                label="Facility *"
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    setFieldValue('session_site_id', '');
                                                }}
                                                onBlur={handleBlur}
                                            >
                                                {facilitySitesData?.map((facility) => (
                                                    <MenuItem key={facility?.facility_id} value={facility?.facility_id}>
                                                        {facility?.facility_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {touched?.facility_id && errors?.facility_id && (
                                                <FormHelperText>{errors?.facility_id}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl
                                            fullWidth
                                            error={touched?.session_site_id && Boolean(errors?.session_site_id)}
                                            disabled={!values?.facility_id}
                                        >
                                            <InputLabel id="session-site-label">Session Site <span className="text-danger">*</span></InputLabel>
                                            <Select
                                                labelId="session-site-label"
                                                id="session_site_id"
                                                name="session_site_id"
                                                value={values?.session_site_id}
                                                label="Session Site *"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            >
                                                {facilitySitesData?.find((f) => f.facility_id === values?.facility_id)?.session_sites?.map((site) => (
                                                    <MenuItem key={site?.session_site_id} value={site?.session_site_id}>
                                                        {site?.session_site_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {touched?.session_site_id && errors?.session_site_id && (
                                                <FormHelperText>{errors?.session_site_id}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid size={12}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                views={["year", "month", "day"]}
                                                label={
                                                    <>
                                                        Date of Last Menstrual Period <span className="text-danger">*</span>
                                                    </>
                                                }
                                                format="DD-MM-YYYY"
                                                value={values?.lmc_date ? dayjs(values?.lmc_date) : null}
                                                minDate={currentConstraints?.lmcMinDate}
                                                maxDate={currentConstraints?.lmcMaxDate}
                                                disabled={isLmcDateDisabled}
                                                onChange={(value) => {
                                                    const formatted = value ? value.format('YYYY-MM-DD') : null;
                                                    setFieldValue('lmc_date', formatted);
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        autoComplete: "off",
                                                        error: touched?.lmc_date && Boolean(errors?.lmc_date),
                                                        helperText: touched?.lmc_date && errors?.lmc_date,
                                                        onBlur: handleBlur,
                                                        name: "lmc_date",
                                                        onDrop: (e) => {
                                                            e.preventDefault();
                                                            return false;
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid size={12}>
                                        <FormControl
                                            fullWidth
                                            error={touched?.blood_group && Boolean(errors?.blood_group)}
                                            disabled={isBloodGroupDisabled}
                                        >
                                            <InputLabel id="blood_group-label">Blood Group <span className="text-danger">*</span> </InputLabel>
                                            <Select
                                                labelId="blood_group-label"
                                                id="blood_group"
                                                name="blood_group"
                                                value={values?.blood_group}
                                                label="Blood Group"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            >
                                                <MenuItem value={"A+ve"}>{"A+ve"}</MenuItem>
                                                <MenuItem value={"B+ve"}>{"B+ve"}</MenuItem>
                                                <MenuItem value={"AB+ve"}>{"AB+ve"}</MenuItem>
                                                <MenuItem value={"O+ve"}>{"O+ve"}</MenuItem>
                                                <MenuItem value={"O-ve"}>{"O-ve"}</MenuItem>
                                                <MenuItem value={"A-ve"}>{"A-ve"}</MenuItem>
                                                <MenuItem value={"B-ve"}>{"B-ve"}</MenuItem>
                                                <MenuItem value={"AB-ve"}>{"AB-ve"}</MenuItem>
                                            </Select>
                                            {touched?.blood_group && errors?.blood_group && (
                                                <FormHelperText>{errors?.blood_group}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid size={12}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                views={["year", "month", "day"]}
                                                label="Date of Last Pregnancy Outcome"
                                                format="DD-MM-YYYY"
                                                value={values?.previous_pregnancy_year ? dayjs(values?.previous_pregnancy_year) : null}
                                                minDate={currentConstraints?.prevPregnancyMinDate}
                                                maxDate={currentConstraints?.prevPregnancyMaxDate}
                                                onChange={(value) => {
                                                    const formatted = value ? value.format('YYYY-MM-DD') : null;
                                                    setFieldValue('previous_pregnancy_year', formatted);
                                                    if (!shouldShowTdDoses(values?.lmc_date, formatted)) {
                                                        setFieldValue('td_doses_received', '0');
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        autoComplete: "off",
                                                        error: touched?.previous_pregnancy_year && Boolean(errors?.previous_pregnancy_year),
                                                        helperText: touched?.previous_pregnancy_year && errors?.previous_pregnancy_year,
                                                        onBlur: handleBlur,
                                                        name: "previous_pregnancy_year",
                                                        onDrop: (e) => {
                                                            e.preventDefault();
                                                            return false;
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    {shouldShowTdDoses(values?.lmc_date, values?.previous_pregnancy_year) && (
                                        <Grid size={12}>
                                            <FormControl
                                                fullWidth
                                                component="fieldset"
                                                error={touched?.td_doses_received && Boolean(errors?.td_doses_received)}
                                            >
                                                <Typography sx={{ mb: 1 }}>
                                                    Number of Td doses received in last pregnancy *
                                                </Typography>

                                                <RadioGroup
                                                    name="td_doses_received"
                                                    value={values?.td_doses_received}
                                                    onChange={handleChange}
                                                >
                                                    {/* Td doses */}
                                                    <Box
                                                        sx={{
                                                            border: "1px solid #ccc",
                                                            borderRadius: 1,
                                                            p: 2,
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <FormLabel sx={{ mb: 1 }}>Td doses</FormLabel>

                                                        <Box sx={{ display: "flex", gap: 3 }}>
                                                            <FormControlLabel
                                                                value="0"
                                                                control={<Radio />}
                                                                label="Dose 0"
                                                            />
                                                            <FormControlLabel
                                                                value="1"
                                                                control={<Radio />}
                                                                label="Dose 1"
                                                            />
                                                            <FormControlLabel
                                                                value="2"
                                                                control={<Radio />}
                                                                label="Dose 2"
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Booster */}
                                                    <Box
                                                        sx={{
                                                            border: "1px solid #ccc",
                                                            borderRadius: 1,
                                                            p: 2,
                                                        }}
                                                    >
                                                        <FormLabel sx={{ mb: 1 }}>Td Booster</FormLabel>

                                                        <Box sx={{ display: "flex", gap: 3 }}>
                                                            <FormControlLabel
                                                                value="3"
                                                                control={<Radio />}
                                                                label="Booster"
                                                            />
                                                        </Box>
                                                    </Box>
                                                </RadioGroup>
                                                {touched?.td_doses_received && errors?.td_doses_received && (
                                                    <FormHelperText error>{errors?.td_doses_received}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    )}
                                </Grid>

                                <DialogActions sx={{ px: 0, pt: 3 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </Button>
                                </DialogActions>
                            </Form>
                        );
                    }}
                </Formik>
            </DialogContent>
        </Dialog>
    )
}

export default DeclarePregnancy